// Global Instructions Rule Applied!
// Distribution waterfall cfg lookups surfaced through get_global_configs_v3
// (docs/WATERFALL_CARRY_DESIGN.md, phase 1): cfg.waterfall_bases,
// cfg.waterfall_rate_sources, the five label-keyed lookups (tier types, stop
// metrics, stop bases, recipient classes, scopes), cfg.waterfall_templates and
// cfg.waterfall_history_checks (phase 6: how a waterfall checks its earlier
// distributions). They feed the Terms tab Waterfall section. Mirrors
// backend/src/data_manager/data_models/waterfall_lookups_model.py.
import { z } from 'zod'

// cfg.waterfall_bases: fund (the stop tests run on the LP class in aggregate)
// or commitment (on the LP being paid)
export const WaterfallBasisModelSchema = z
  .object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.waterfall_rate_sources: where a split's rate or a tier's threshold
// comes from - fixed, remainder, or a carry column of the LP's commitment
export const WaterfallRateSourceModelSchema = z
  .object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    // 'rate' | 'threshold' | 'both'
    applies_to: z.string(),
    description: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

// the five label-keyed lookups
export const WaterfallLookupModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.waterfall_history_checks: how a new distribution checks the earlier
// ones - to_date, as_distributed, none. description and example are what the
// "?" beside the select shows
export const WaterfallHistoryCheckModelSchema = z
  .object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    example: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.waterfall_templates: a shape to copy onto a fund (Create from template)
export const WaterfallTemplateModelSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullish(),
    basis_id: z.number().nullish(),
    scope_id: z.number().nullish(),
    // the history check a waterfall created from the template starts with
    history_check_id: z.number().nullish(),
    recallable_pct: z.number().nullish(),
    tier_count: z.number(),
    display_order: z.number().nullish()
  })
  .strict()

// phase 3: the Distribution transaction's lookups
// cfg.distribution_statuses: Draft / Issued
export const DistributionStatusModelSchema = z
  .object({
    id: z.number(),
    label: z.string(),
    display_order: z.number().nullish()
  })
  .strict()

// cfg.distribution_kinds: proceeds, return_of_capital, special_distribution,
// tax_advance, in_kind (the last two reserved - the engine refuses them)
export const DistributionKindModelSchema = z
  .object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    display_order: z.number().nullish()
  })
  .strict()

export type WaterfallBasis = z.infer<typeof WaterfallBasisModelSchema>
export type WaterfallRateSource = z.infer<typeof WaterfallRateSourceModelSchema>
export type WaterfallLookup = z.infer<typeof WaterfallLookupModelSchema>
export type WaterfallTemplate = z.infer<typeof WaterfallTemplateModelSchema>
export type WaterfallHistoryCheck = z.infer<typeof WaterfallHistoryCheckModelSchema>
export type DistributionStatus = z.infer<typeof DistributionStatusModelSchema>
export type DistributionKind = z.infer<typeof DistributionKindModelSchema>
